// =====================================================
// EVENT GUESTS SERVICE
// Stage 2: Participants & Pricing
// CRUD operations for event guests
// =====================================================

import { supabase } from '@/supabase.config';

// ===================
// TYPES
// ===================

export interface EventGuest {
  id: string;
  full_name: string;
  dni: string | null;
  email: string | null;
  phone: string | null;
  age: number | null;
  church_affiliation: string | null;
  representing_minister_id: string | null;
  is_delegate?: boolean;
  represented_name?: string | null;
  represented_ci?: string | null;
  institution_note?: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateEventGuestData {
  full_name: string;
  dni?: string;
  email?: string;
  phone?: string;
  age?: number;
  church_affiliation?: string;
  representing_minister_id?: string;
  /** Attends as delegate of a minister / church / institution */
  is_delegate?: boolean;
  /** Who is represented when it isn't a known minister */
  represented_name?: string;
  represented_ci?: string;
  institution_note?: string;
}

export interface UpdateEventGuestData {
  full_name?: string;
  dni?: string;
  email?: string;
  phone?: string;
  age?: number;
  church_affiliation?: string;
  representing_minister_id?: string;
  is_delegate?: boolean;
  represented_name?: string | null;
  represented_ci?: string | null;
  institution_note?: string | null;
}

// ===================
// CRUD OPERATIONS
// ===================

/**
 * Create a new event guest
 */
export async function createEventGuest(data: CreateEventGuestData): Promise<EventGuest> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data: guest, error } = await supabase
    .from('event_guests')
    .insert({
      full_name: data.full_name,
      dni: data.dni || null,
      email: data.email || null,
      phone: data.phone || null,
      age: data.age || null,
      church_affiliation: data.church_affiliation || null,
      representing_minister_id: data.representing_minister_id || null,
      is_delegate: data.is_delegate ?? false,
      represented_name: data.represented_name || null,
      represented_ci: data.represented_ci || null,
      institution_note: data.institution_note || null,
      created_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating event guest:', error);
    throw new Error(`Error al crear invitado: ${error.message}`);
  }

  return guest;
}

/**
 * Reuse the guest with the same CI (dedupe), refreshing contact data;
 * create one when there's no CI match. Prevents duplicate guest rows when the
 * same person registers again or a registration attempt failed midway.
 */
export async function findOrCreateEventGuest(data: CreateEventGuestData): Promise<EventGuest> {
  const dni = data.dni?.trim();
  if (dni) {
    const { data: existing, error } = await supabase
      .from('event_guests')
      .select('*')
      .eq('dni', dni)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Error al buscar invitado: ${error.message}`);
    }
    if (existing) {
      return updateEventGuest(existing.id, {
        full_name: data.full_name,
        email: data.email || existing.email || undefined,
        phone: data.phone || existing.phone || undefined,
        age: data.age ?? existing.age ?? undefined,
        church_affiliation: data.church_affiliation || existing.church_affiliation || undefined,
        representing_minister_id: data.representing_minister_id || existing.representing_minister_id || undefined,
        // The delegate flag belongs to the person: an explicit true/false from the caller wins
        // (a delegate who later registers as a plain guest must not stay a delegate).
        // Callers that don't say (undefined) leave it untouched.
        ...(data.is_delegate === true
          ? {
              is_delegate: true,
              represented_name: data.represented_name || undefined,
              represented_ci: data.represented_ci || undefined,
              institution_note: data.institution_note || undefined,
            }
          : data.is_delegate === false
            ? { is_delegate: false, represented_name: null, represented_ci: null, institution_note: null }
            : {}),
      });
    }
  }
  return createEventGuest({ ...data, dni });
}

/**
 * Get event guest by ID
 */
export async function getEventGuestById(id: string): Promise<EventGuest | null> {
  const { data, error } = await supabase.from('event_guests').select('*').eq('id', id).single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching event guest:', error);
    throw new Error(`Error al cargar invitado: ${error.message}`);
  }

  return data;
}

/**
 * Update an event guest
 */
export async function updateEventGuest(id: string, data: UpdateEventGuestData): Promise<EventGuest> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data: guest, error } = await supabase
    .from('event_guests')
    .update({
      ...data,
      updated_by: user?.id || null,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating event guest:', error);
    throw new Error(`Error al actualizar invitado: ${error.message}`);
  }

  return guest;
}

/**
 * Delete an event guest
 */
export async function deleteEventGuest(id: string): Promise<void> {
  const { error } = await supabase.from('event_guests').delete().eq('id', id);

  if (error) {
    console.error('Error deleting event guest:', error);
    throw new Error(`Error al eliminar invitado: ${error.message}`);
  }
}

/**
 * Search for guests by name or email
 */
export async function searchEventGuests(query: string): Promise<EventGuest[]> {
  if (!query || query.length < 2) {
    return [];
  }

  const searchLower = query.toLowerCase();

  const { data, error } = await supabase
    .from('event_guests')
    .select('*')
    .or(`full_name.ilike.%${searchLower}%,email.ilike.%${searchLower}%`)
    .order('full_name', { ascending: true })
    .limit(20);

  if (error) {
    console.error('Error searching event guests:', error);
    throw new Error('Error al buscar invitados');
  }

  return data || [];
}
